import React, { useState, useEffect, useRef } from 'react';
import './SeoContent.css';
import api from '../../api/axios';

const PAGE_OPTIONS = [
  { slug: 'home', label: 'Home Page' },
  { slug: 'about', label: 'About Page' },
  { slug: 'vendors', label: 'Vendors Page' },
  { slug: 'blog', label: 'Blog Page' },
  { slug: 'contact', label: 'Contact Page' },
  { slug: 'privacy', label: 'Privacy Policy' },
  { slug: 'terms', label: 'Terms & Conditions' },
  { slug: 'faq', label: 'FAQ Page' },
  { slug: 'help', label: 'Help & Support' },
  { slug: 'handpicked-vendors', label: 'Handpicked Vendors' },
  { slug: 'budget-calculator', label: 'Budget Calculator' },
];

const EMPTY_FORM = { pageSlug: '', pageLabel: '', title: '', content: '', isActive: true };

const SeoContentManagement = () => {
  const [seoList, setSeoList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useCustomSlug, setUseCustomSlug] = useState(false);
  const modalRef = useRef(null);

  const fetchSeoContents = async () => {
    try {
      const res = await api.get('/admin/seo-content');
      setSeoList(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchSeoContents(); }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (showModal && modalRef.current && !modalRef.current.contains(e.target)) resetForm();
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showModal]);

  const resetForm = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setUseCustomSlug(false);
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setForm({
      pageSlug: item.pageSlug,
      pageLabel: item.pageLabel || '',
      title: item.title || '',
      content: item.content || '',
      isActive: item.isActive !== false,
    });
    const isPreset = PAGE_OPTIONS.some(p => p.slug === item.pageSlug);
    setUseCustomSlug(!isPreset);
    setShowModal(true);
  };

  const handlePageSelect = (e) => {
    const slug = e.target.value;
    if (slug === '__custom__') {
      setUseCustomSlug(true);
      setForm(f => ({ ...f, pageSlug: '', pageLabel: '' }));
    } else {
      const found = PAGE_OPTIONS.find(p => p.slug === slug);
      setUseCustomSlug(false);
      setForm(f => ({ ...f, pageSlug: slug, pageLabel: found?.label || '' }));
    }
  };

  const handleSubmit = async () => {
    if (!form.pageSlug.trim() || !form.content.trim()) {
      alert('Page slug and content are required');
      return;
    }
    setLoading(true);
    try {
      await api.put('/admin/seo-content', form);
      resetForm();
      fetchSeoContents();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save SEO content');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/seo-content/${deleteTarget._id}`);
      fetchSeoContents();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteTarget(null);
      setShowDeleteModal(false);
    }
  };

  const currentPageSlug = useCustomSlug ? '__custom__' : (form.pageSlug || '');

  return (
    <div className="seo-management">
      <div className="page-header">
        <h1>SEO Content</h1>
        <p>Manage SEO text content displayed above the footer on each page</p>
      </div>

      <div className="seo-controls">
        <button className="add-btn" onClick={() => setShowModal(true)}>+ Add SEO Content</button>
      </div>

      <div className="seo-grid">
        {seoList.map((item) => (
          <div key={item._id} className={`seo-card ${!item.isActive ? 'inactive' : ''}`}>
            <div className="seo-card-header">
              <div className="seo-card-slug-wrap">
                <span className="page-slug-badge">{item.pageSlug}</span>
                {item.pageLabel && <span className="page-label">{item.pageLabel}</span>}
              </div>
              <span className={`active-badge ${item.isActive ? 'active' : 'inactive'}`}>
                {item.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {item.title && <h3 className="seo-card-title">{item.title}</h3>}
            <p className="seo-card-content">
              {item.content?.replace(/<[^>]+>/g, ' ').substring(0, 160)}
              {item.content?.length > 160 ? '...' : ''}
            </p>
            <div className="seo-card-actions">
              <button className="btn-edit" onClick={() => handleEdit(item)}>Edit</button>
              <button className="btn-delete" onClick={() => { setDeleteTarget(item); setShowDeleteModal(true); }}>Delete</button>
            </div>
          </div>
        ))}
        {seoList.length === 0 && (
          <div className="no-data">No SEO content added yet. Click "Add SEO Content" to get started.</div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="seo-modal" ref={modalRef}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit SEO Content' : 'Add SEO Content'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Page *</label>
                <select value={currentPageSlug} onChange={handlePageSelect} disabled={!!editingId}>
                  <option value="">Select a page</option>
                  {PAGE_OPTIONS.map(p => (
                    <option key={p.slug} value={p.slug}>{p.label}</option>
                  ))}
                  <option value="__custom__">Custom page slug...</option>
                </select>
              </div>

              {useCustomSlug && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Custom Page Slug *</label>
                    <input
                      value={form.pageSlug}
                      onChange={(e) => setForm({ ...form, pageSlug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      placeholder="e.g. category-photography"
                      disabled={!!editingId}
                    />
                  </div>
                  <div className="form-group">
                    <label>Page Label</label>
                    <input
                      value={form.pageLabel}
                      onChange={(e) => setForm({ ...form, pageLabel: e.target.value })}
                      placeholder="e.g. Photography Category"
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Section Title</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Optional heading for the SEO section"
                />
              </div>

              <div className="form-group">
                <label>SEO Content * <span className="hint">(HTML supported)</span></label>
                <textarea
                  rows={8}
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Enter the SEO content for this page. HTML tags like <h2>, <p>, <strong>, <ul>, <li> are supported."
                />
              </div>

              <div className="form-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  &nbsp; Active (show on website)
                </label>
              </div>

              <div className="preview-box">
                <div className="preview-label">Live Preview</div>
                {form.title && <h3 className="preview-title">{form.title}</h3>}
                <div className="preview-content" dangerouslySetInnerHTML={{ __html: form.content || '<em style="color:#9ca3af">No content yet...</em>' }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={resetForm}>Cancel</button>
              <button className="save-btn" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Saving...' : 'Save SEO Content'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <h3>Delete SEO Content</h3>
            <p>Delete SEO content for page "<strong>{deleteTarget?.pageSlug}</strong>"?</p>
            <div className="delete-actions">
              <button className="cancel-btn" onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}>Cancel</button>
              <button className="confirm-delete-btn" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeoContentManagement;
